// Workflow node types for the Launch Builder canvas
import React from "react";
import { Handle, Position } from "reactflow";

// Native React representation of D2 Nodes to prevent WASM CPU lockup
const D2StyledNode = ({ label, subLabel, bottomText }) => (
  <div className="w-[160px] h-[60px] bg-white rounded-[6px] border-[1.5px] border-[#23346e] flex flex-col items-center justify-center shadow-sm hover:shadow-md transition-shadow">
    {subLabel && (
      <div className="text-[9px] font-bold text-[#23346e] opacity-70 uppercase tracking-widest leading-none mb-0.5">
        {subLabel}
      </div>
    )}
    <div className="text-[13px] font-bold text-[#23346e] text-center w-full truncate px-2 leading-tight" style={{ fontFamily: "ui-sans-serif, system-ui, -apple-system, sans-serif" }}>
      {label}
    </div>
    <div className="text-[10px] font-semibold text-[#23346e] opacity-70 italic truncate max-w-full px-2 mt-0.5">
      {bottomText || "Select platform"}
    </div>
  </div>
);

// ─── Trigger Node ─────────────────────────────────────────────────────────────
export const TriggerNode = ({ data }) => (
  <div className="relative">
    <D2StyledNode label={data.label || 'Trigger'} subLabel="Trigger" bottomText={data.platform} />
    <Handle type="source" position={Position.Bottom} />
  </div>
);

// ─── Connector Node ───────────────────────────────────────────────────────────
export const ConnectorNode = ({ data }) => (
  <div className="relative">
    <Handle type="target" position={Position.Top} />
    <D2StyledNode label={data.label || 'Connector'} subLabel="Connector" bottomText={data.platform} />
    <Handle type="source" position={Position.Bottom} />
  </div>
);

// ─── Router Node ──────────────────────────────────────────────────────────────
export const RouterNode = ({ data }) => (
  <div className="relative">
    <Handle type="target" position={Position.Top} />
    <D2StyledNode label={data.label || 'Router'} subLabel="Router" bottomText="Conditional branching" />
    <Handle type="source" position={Position.Bottom} id="true" />
    <Handle type="source" position={Position.Bottom} id="false" />
  </div>
);

// ─── Aggregator Node ──────────────────────────────────────────────────────────
export const AggregatorNode = ({ data }) => (
  <div className="relative">
    <Handle type="target" position={Position.Top} id="in0" />
    <Handle type="target" position={Position.Top} id="in1" />
    <Handle type="target" position={Position.Top} id="in2" />
    <D2StyledNode label={data.label || 'Aggregator'} subLabel="Aggregator" bottomText={data.strategy || "merge"} />
    <Handle type="source" position={Position.Bottom} />
  </div>
);

// ─── Transformer Node ─────────────────────────────────────────────────────────
export const TransformerNode = ({ data }) => {
  const count = data.transformRules?.length || 0;
  const firstRule = data.transformRules?.[0];
  let preview = `${count} rules`;
  
  if (count > 0 && firstRule?.field) {
     if (firstRule.targetField) {
         preview = `${firstRule.field} → ${firstRule.targetField}`;
         if (count > 1) preview += ` (+${count-1})`;
     } else {
         preview = `${count} rules (${firstRule.field}... )`;
     }
  }
  
  return (
    <div className="relative">
      <Handle type="target" position={Position.Top} />
      <D2StyledNode label={data.label || 'Transformer'} subLabel="Transformer" bottomText={preview} />
      <Handle type="source" position={Position.Bottom} />
    </div>
  );
};

// ─── Sequential Connector Node (NEW — does not affect existing node types) ────
// Used only in sequential (A → B → C → N) pipeline views.
// Shows step number badge and receive/pass labels for payload enrichment clarity.
export const SequentialConnectorNode = ({ data }) => {
  const stepNum = data.stepOrder !== undefined ? data.stepOrder + 1 : null;
  const receivesFrom = data.receivesFrom || null;
  const passesTo = data.passesTo || null;

  return (
    <div className="relative">
      <Handle type="target" position={Position.Left} />
      <div className="w-[180px] bg-white rounded-[6px] border-[2px] border-[#2563eb] flex flex-col items-center justify-center shadow-sm hover:shadow-md transition-shadow py-1">
        {stepNum !== null && (
          <div className="absolute -top-3 left-1/2 -translate-x-1/2 bg-[#2563eb] text-white text-[9px] font-bold px-2 py-0.5 rounded-full">
            Step {stepNum}
          </div>
        )}
        {receivesFrom && (
          <div className="text-[8px] text-[#2563eb] opacity-70 w-full px-2 truncate text-left leading-none mb-0.5">
            ← {receivesFrom}
          </div>
        )}
        <div className="text-[9px] font-bold text-[#1e3a8a] opacity-70 uppercase tracking-widest leading-none">
          Connector
        </div>
        <div className="text-[13px] font-bold text-[#1e3a8a] text-center w-full truncate px-2 leading-tight" style={{ fontFamily: "ui-sans-serif, system-ui, -apple-system, sans-serif" }}>
          {data.label || "Step"}
        </div>
        <div className="text-[10px] font-semibold text-[#1e3a8a] opacity-70 italic truncate max-w-full px-2 mt-0.5">
          {data.platform || "Select platform"}
        </div>
        {passesTo && (
          <div className="text-[8px] text-[#2563eb] opacity-70 w-full px-2 truncate text-right leading-none mt-0.5">
            → {passesTo}
          </div>
        )}
      </div>
      <Handle type="source" position={Position.Right} />
    </div>
  );
};

export const PIPELINE_NODE_TYPES = {
  trigger: TriggerNode,
  connector: ConnectorNode,
  router: RouterNode,
  aggregator: AggregatorNode,
  transformer: TransformerNode,
  // Sequential node — registered here so ReactFlow can render it
  sequential_connector: SequentialConnectorNode,
};
